import { createPortal } from "react-dom";
import type { IOption, ISelect, NormalizeOption } from "./types";
import {
  forwardRef,
  type MouseEvent as ReactMouseEvent,
  type Ref,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from "react";

import TimesIcon from "../../assets/icons/TimesIcon";
import Fuse from "fuse.js";

import { Pagination } from "../Pagination";
import {
  SelectContainer,
  SelectInput,
  SelectOptionsContainer,
  SelectErrorMessage,
} from "./style";

interface IOptionPartial extends IOption {
  hidden?: boolean;
}

const Select = forwardRef(
  <Multiple extends true | undefined, Label extends string | undefined>(
    {
      options,
      multiple,
      defaultValue,
      value,
      forceMobile,
      isLoading,
      clearButton,
      closeOnSelect,
      name,
      onChange,
      label,
      placeholder,
      javascriptSearch,
      searchOnChange,
      disabled,
      required,
      theme,
      changePage,
      pageCount,
      pageSelected,
      isError,
    }: ISelect<Multiple, Label>,
    ref: Ref<HTMLDivElement>,
  ) => {
    const placeholderRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLUListElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<IOption[]>(
      !defaultValue
        ? []
        : Array.isArray(defaultValue)
          ? defaultValue
          : [defaultValue],
    );
    const [selectOptions, setSelectOptions] =
      useState<IOptionPartial[]>(options);
    const [search, setSearch] = useState("");
    const deferredSearch = useDeferredValue(search);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

    theme = theme ? theme : "modern";

    useEffect(() => setSelectOptions(options), [options]);
    useEffect(() => {
      disabled && setIsOpen(false);
    }, [disabled]);

    useEffect(() => {
      if (!options) return;

      // uncontrolled (sem prop value): a seleção interna vem de defaultValue e
      // não pode ser zerada quando options muda de identidade a cada render
      if (value === undefined) return;

      if (value) {
        const valueAsAny = value as any;

        const initialSelected = Array.isArray(value)
          ? options.filter((option) =>
            valueAsAny.includes(option.value)
          )
          : options.find((option) =>
            option.value === valueAsAny
          );

        setSelectedOptions(
          Array.isArray(initialSelected)
            ? initialSelected
            : initialSelected
              ? [initialSelected]
              : [],
        );
      } else {
        setSelectedOptions([]);
      }
    }, [value, options]);

    useEffect(() => {
      const handleAsideClickOutside = (e: MouseEvent) => {
        if (
          !placeholderRef.current?.contains(e.target as Node) &&
          !optionsRef.current?.contains(e.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      window.addEventListener("click", handleAsideClickOutside);
      return () => {
        window.removeEventListener("click", handleAsideClickOutside);
      };
    }, []);

    const getOptionLabelByFirstValue = useCallback(() => {
      const label = selectedOptions.at(-1)?.label;

      if (label) {
        return label;
      }

      const optionByValue = options.find(
        (options) => options.value === selectedOptions.at(-1)?.value,
      );

      if (optionByValue?.label) {
        return optionByValue.label;
      }

      return optionByValue?.value;
    }, [selectedOptions, options]);

    const handleFuseSearch = useCallback(() => {
      if (deferredSearch && javascriptSearch && !searchOnChange) {
        const fuse = new Fuse(selectOptions, {
          keys: ["label"],
          threshold: 0.2,
        });
        const searchResultValues = fuse
          .search(deferredSearch)
          .map((result: { item: IOptionPartial; }) => result.item)
          .map((item: { value: string | number; }) => item.value);
        setSelectOptions((prev) =>
          [...prev].map((option) => ({
            ...option,
            hidden: !searchResultValues.includes(option.value),
          })),
        );

        return;
      }

      setSelectOptions((prev) =>
        [...prev].map((option) => ({ ...option, hidden: false })),
      );
    }, [deferredSearch, selectOptions, javascriptSearch, searchOnChange]);

    useEffect(
      () => handleFuseSearch(),
      [deferredSearch, javascriptSearch, searchOnChange],
    );

    const handleToggleOptionSelect = (option: IOption) => {
      let updatedOptions: IOption[] = [];

      if (!multiple) {
        updatedOptions = selectedOptions.includes(option) ? [] : [option];
      }

      if (multiple) {
        updatedOptions = selectedOptions
          .map((prevOption) => prevOption.value)
          .includes(option.value)
          ? selectedOptions.filter(
            (selectedOption) => selectedOption.value !== option.value,
          )
          : [...selectedOptions, option];
      }

      setSelectedOptions(updatedOptions);

      closeOnSelect && setIsOpen(false);
      onChange &&
        onChange(
          (multiple
            ? updatedOptions
            : updatedOptions[0]) as NormalizeOption<Multiple>,
        );
    };

    const handleClearSelections = (e: ReactMouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      setSelectedOptions([]);
      onChange &&
        onChange((multiple ? [] : undefined) as unknown as NormalizeOption<Multiple>);
    };

    const selectedOptionsValues = selectedOptions.map((option) => option.value);

    useEffect(() => {
      const updatePosition = () => {
        if (isOpen && placeholderRef.current) {
          const rect = placeholderRef.current.getBoundingClientRect();

          setPosition((prev) => ({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: prev.width,
          }));
        }
      };

      if (isOpen) {
        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
      }

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
      };
    }, [isOpen, placeholderRef]);

    useEffect(() => {
      const updatePosition = () => {
        if (isOpen && placeholderRef.current) {
          const placeholderWidth = placeholderRef.current.offsetWidth;
          setPosition((prev) => ({ ...prev, width: placeholderWidth }));
        }
      };

      if (isOpen) {
        updatePosition();
        window.addEventListener("resize", updatePosition);
      }

      return () => {
        window.removeEventListener("resize", updatePosition);
      };
    }, [isOpen, placeholderRef]);

    const getDisplayValue = () => {
      if (selectedOptions.length === 0) {
        return placeholder ?? "Selecione";
      }
      if (selectedOptions.length === 1) {
        // opções podem vir só com value (ex.: tipos de campo do relatório)
        return selectedOptions[0].label ?? selectedOptions[0].value;
      }
      return `${selectedOptions.length} itens selecionados`;
    };

    const displayValue = getDisplayValue();


    return (
      <SelectContainer
        ref={ref}
        $theme={theme}
        $isLoading={isLoading}
        $disabled={disabled}
        $hasValue={selectedOptions.length > 0}
      >
        {label && <label>{label}</label>}
        <SelectInput
          $theme={theme}
          $isLoading={isLoading}
          className="select_input"
          onClick={() => setIsOpen((prev) => !prev)}
          ref={placeholderRef}
        >
          <div
            className={`placeholder ${!getOptionLabelByFirstValue() ? "--default_placeholder" : ""}`}
          >
            {displayValue}
          </div>
          <div
            className="multiple_counter"
            content={
              multiple && selectedOptions.length > 1
                ? selectedOptions.length.toString()
                : undefined
            }
          />
          {!disabled && !isLoading && clearButton && (
            <button
              onClick={handleClearSelections}
              className="--clear"
              type="button"
              children={<TimesIcon />}
            />
          )}
          {required && !disabled ? (
            // input só para validação nativa do form: invisível e sem interação,
            // mas renderizado (display:none bloquearia o submit sem mensagem)
            <input
              className="required"
              required
              onChange={() => { }}
              value={selectedOptions.length > 0 ? "has" : ""}
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                pointerEvents: "none",
                border: 0,
              }}
            />
          ) : (
            ""
          )}
        </SelectInput>
        {createPortal(
          <SelectOptionsContainer
            $theme={theme}
            $forceMobile={forceMobile}
          >
            <div
              className="overlay"
              ref={overlayRef}
              onClick={(e) =>
                e.target === overlayRef.current && setIsOpen(false)
              }
            >
              <ul
                className={`options ${isOpen ? "--is_open" : ""}`}
                style={
                  window.innerWidth > 720
                    ? {
                      top: `${position.top}px`,
                      left: `${position.left}px`,
                      width: position.width,
                    }
                    : {}
                }
                ref={optionsRef}
              >
                {(javascriptSearch || searchOnChange) && (
                  <li className="--search">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => {
                        searchOnChange && searchOnChange(e.target.value);
                        setSearch(e.target.value);
                      }}
                      placeholder="Buscar"
                    />
                  </li>
                )}
                {selectOptions.map((option, index) => (
                  <li
                    key={index}
                    className={`${selectedOptionsValues.includes(option.value) ? "--is_selected" : ""
                      } ${option.hidden ? "--hidden" : ""}`}
                    onClick={() => handleToggleOptionSelect(option)}
                    children={option.label ?? option.value}
                  />
                ))}
                {pageSelected !== undefined ? (
                  <Pagination
                    changePage={changePage}
                    pageCount={pageCount}
                    pageSelected={pageSelected}
                  />
                ) : null}
              </ul>
            </div>
          </SelectOptionsContainer>,
          document.body,
        )}
        {name &&
          selectedOptionsValues.map((optionValue, index) => (
            <input
              type="hidden"
              key={index}
              value={optionValue}
              name={name}
            />
          ))}
        {isError ? (
          <SelectErrorMessage>{isError?.message}</SelectErrorMessage>
        ) : null}
      </SelectContainer>
    );
  },
);

export default Select;
